<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    onMounted,
    ref,
    watch,
  } from 'vue';
  import { ClubService } from '@/api/services/club.service';
  import V2ModalClubProgram from '@/components/modals/club/V2ModalClubProgram.vue';
  import { programToUpsertDto } from '@/components/modals/club/club-program-draft';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import V2ClubMembersTab from '@/components/v2/club/V2ClubMembersTab.vue';
  import V2ClubMovementsTab from '@/components/v2/club/V2ClubMovementsTab.vue';
  import V2ClubSummaryTab from '@/components/v2/club/V2ClubSummaryTab.vue';
  import { useAuth } from '@/composition/use-auth';
  import { useModal } from '@/composition/use-modal';
  import { useSocketStore } from '@/composition/use-socket';
  import { useUi } from '@/composition/use-ui';
  import { PermissionType } from '@/enums/PermissionType';
  import { AlertType } from '@/interfaces/IAlert';
  import { ClubProgramStatus, IClubProgram } from '@/interfaces/IClubProgram';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { createQrBase64 } from '@/utils';

  const auth = useAuth();
  const modals = useModal();
  const ui = useUi();
  const restaurantStore = useRestaurantStore();
  const socketStore = useSocketStore();
  const proxy = getCurrentInstance()!.proxy;

  /** ClubTopup-only users get a read+topup view: members list, no management. */
  const canManage = computed(() =>
    auth.hasPermission(PermissionType.ClubManage)
  );

  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  //#region Program

  const program = ref<IClubProgram | null>(null);
  const loaded = ref(false);

  const isPaused = computed(
    () => program.value?.status === ClubProgramStatus.Paused
  );

  async function loadProgram() {
    const restaurantId = restaurantStore.restaurant?.id;
    if (!restaurantId) return;

    try {
      loaded.value = false;
      program.value = await ClubService.getProgram(restaurantId);
    } catch (e) {
      ui.serverError(e, i18nT('club.page.loadError'));
    } finally {
      loaded.value = true;
    }
  }

  watch(() => restaurantStore.restaurant?.id, loadProgram, { immediate: true });

  //#endregion

  //#region Websocket refresh (clubU: program settings changed elsewhere)

  const wsEvent = computed(() => {
    const restaurant = restaurantStore.restaurant;
    return restaurant ? `r:${restaurant.id}:clubU` : null;
  });

  function onClubProgramWs() {
    // Payload is the PUBLIC program mapping; this page edits the full admin
    // program, so refetch it instead of consuming the payload directly.
    loadProgram();
    summaryTabRef.value?.reload();
  }

  let boundWsEvent: string | null = null;

  function bindWs() {
    unbindWs();
    if (wsEvent.value && socketStore.socket) {
      socketStore.socket.on(wsEvent.value, onClubProgramWs);
      boundWsEvent = wsEvent.value;
    }
  }

  function unbindWs() {
    if (boundWsEvent && socketStore.socket) {
      socketStore.socket.off(boundWsEvent, onClubProgramWs);
    }
    boundWsEvent = null;
  }

  onMounted(bindWs);
  onBeforeUnmount(unbindWs);
  watch(wsEvent, bindWs);

  //#endregion

  //#region Tabs

  const activeTab = ref<'summary' | 'members' | 'movements'>(
    auth.hasPermission(PermissionType.ClubManage) ? 'summary' : 'members'
  );
  const summaryTabRef = ref<InstanceType<typeof V2ClubSummaryTab> | null>(null);

  //#endregion

  //#region Actions

  function openProgramWizard() {
    modals.show(
      V2ModalClubProgram,
      { programToEdit: program.value },
      {
        name: 'v2-club-program',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        width: 720,
        maxWidth: Math.round(window.innerWidth * 0.95),
        clickToClose: false,
      },
      {
        saved: (saved: IClubProgram) => {
          program.value = saved;
          summaryTabRef.value?.reload();
        },
      }
    );
  }

  const togglingStatus = ref(false);

  async function toggleStatus() {
    if (!program.value || togglingStatus.value) return;

    const pausing = !isPaused.value;
    if (pausing) {
      const confirmed = await modals.confirm({
        title: i18nT('club.page.pauseTitle'),
        message: i18nT('club.page.pauseMessage'),
        acceptText: i18nT('club.page.pauseCta'),
        cancelText: i18nT('cancel'),
      });
      if (!confirmed) return;
    }

    try {
      togglingStatus.value = true;
      const dto = programToUpsertDto(program.value);
      dto.status = pausing
        ? ClubProgramStatus.Paused
        : ClubProgramStatus.Active;
      program.value = await ClubService.saveProgram(
        restaurantStore.restaurant!.id,
        dto
      );
      ui.showToast(
        AlertType.Success,
        i18nT(pausing ? 'club.page.pausedToast' : 'club.page.resumedToast')
      );
    } catch (e) {
      ui.serverError(e, i18nT('club.page.statusError'));
    } finally {
      togglingStatus.value = false;
    }
  }

  /** Join-placard QR: encodes the public store URL customers already use. */
  async function downloadQr() {
    const restaurant = restaurantStore.restaurant;
    if (!restaurant) return;

    try {
      const dataUrl = await createQrBase64(
        `https://bistri.fi/go/${restaurant.id}`,
        600
      );
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `club-qr-${restaurant.stringId}.png`;
      link.click();
    } catch (e) {
      ui.serverError(e, i18nT('club.page.qrError'));
    }
  }

  //#endregion
</script>

<template>
  <div class="page-club">
    <div class="page-club-inner">
      <!-- Loading -->
      <template v-if="!loaded">
        <PosUiSkeleton class="skeleton-header" />
        <PosUiSkeleton class="skeleton-body" />
      </template>

      <!-- No program yet -->
      <div v-else-if="!program" class="club-hero">
        <PosUiEmptyState
          icon="star-circle-outline"
          :title="$t('club.page.heroTitle')"
          :description="$t('club.page.heroDescription')"
        >
          <template #action>
            <PosUiButton
              v-if="canManage"
              color="primary"
              size="lg"
              @click="openProgramWizard"
            >
              {{ $t('club.page.heroCta') }}
            </PosUiButton>
          </template>
        </PosUiEmptyState>

        <div class="hero-benefits">
          <div class="hero-benefit">
            <PosUiIcon name="star-outline" class="hero-benefit-icon" />
            <span>{{ $t('club.page.heroBenefitPoints') }}</span>
          </div>
          <div class="hero-benefit">
            <PosUiIcon name="wallet-outline" class="hero-benefit-icon" />
            <span>{{ $t('club.page.heroBenefitBalance') }}</span>
          </div>
          <div class="hero-benefit">
            <PosUiIcon name="account-heart-outline" class="hero-benefit-icon" />
            <span>{{ $t('club.page.heroBenefitLoyalty') }}</span>
          </div>
        </div>
      </div>

      <!-- Program dashboard -->
      <template v-else>
        <div class="club-command-bar">
          <div class="command-identity">
            <h1 class="command-title">{{ program.name }}</h1>
            <PosUiBadge
              :color="isPaused ? 'warning' : 'success'"
              variant="soft"
              size="sm"
            >
              {{
                isPaused
                  ? $t('club.page.statusPaused')
                  : $t('club.page.statusActive')
              }}
            </PosUiBadge>
          </div>

          <div class="command-actions">
            <div v-if="canManage" class="tab-control">
              <button
                class="tab-option"
                :class="{ active: activeTab === 'summary' }"
                type="button"
                @click="activeTab = 'summary'"
              >
                {{ $t('club.page.tabSummary') }}
              </button>
              <button
                class="tab-option"
                :class="{ active: activeTab === 'members' }"
                type="button"
                @click="activeTab = 'members'"
              >
                {{ $t('club.page.tabMembers') }}
              </button>
              <button
                class="tab-option"
                :class="{ active: activeTab === 'movements' }"
                type="button"
                @click="activeTab = 'movements'"
              >
                {{ $t('club.page.tabMovements') }}
              </button>
            </div>

            <PosUiButton
              v-if="canManage"
              color="primary"
              icon="pencil"
              @click="openProgramWizard"
            >
              {{ $t('club.page.editCta') }}
            </PosUiButton>

            <PosUiPopover placement="bottom" align="end">
              <template #trigger="{ toggle }">
                <PosUiButton
                  color="neutral"
                  variant="outline"
                  icon="dots-horizontal"
                  :loading="togglingStatus"
                  @click="toggle"
                />
              </template>
              <template #default="{ close }">
                <div class="more-menu">
                  <button
                    class="more-menu-item"
                    type="button"
                    @click="
                      close();
                      downloadQr();
                    "
                  >
                    <PosUiIcon name="qrcode" />
                    {{ $t('club.page.downloadQr') }}
                  </button>
                  <button
                    v-if="canManage"
                    class="more-menu-item"
                    type="button"
                    @click="
                      close();
                      toggleStatus();
                    "
                  >
                    <PosUiIcon :name="isPaused ? 'play' : 'pause'" />
                    {{
                      isPaused
                        ? $t('club.page.resumeCta')
                        : $t('club.page.pauseCta')
                    }}
                  </button>
                </div>
              </template>
            </PosUiPopover>
          </div>
        </div>

        <div v-if="isPaused" class="paused-banner">
          <PosUiIcon name="pause-circle-outline" class="paused-banner-icon" />
          {{ $t('club.page.pausedBanner') }}
        </div>

        <div class="club-tab-body">
          <!-- v-if (not just v-show): mounting fires the reports request,
               which 401s for topup-only users. -->
          <V2ClubSummaryTab
            v-if="canManage"
            v-show="activeTab === 'summary'"
            ref="summaryTabRef"
            :program="program"
          />
          <V2ClubMembersTab
            v-if="activeTab === 'members'"
            :program="program"
          />
          <!-- canManage: defense in depth — the tab button is already hidden
               for non-managers, and the API is ClubManage-only. -->
          <V2ClubMovementsTab
            v-if="canManage && activeTab === 'movements'"
            :program="program"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .page-club {
    @apply tw-flex tw-justify-center tw-px-4 tw-py-4 md:tw-px-6;

    .page-club-inner {
      @apply tw-flex tw-w-full tw-max-w-7xl tw-flex-col tw-gap-4;

      .skeleton-header {
        @apply tw-h-14 tw-rounded-xl;
      }

      .skeleton-body {
        @apply tw-h-72 tw-rounded-xl;
      }

      .club-hero {
        @apply tw-flex tw-flex-col tw-gap-6 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-px-6 tw-py-12;

        .hero-benefits {
          @apply tw-mx-auto tw-flex tw-max-w-2xl tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-gap-8;

          .hero-benefit {
            @apply tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-neutral-600;

            .hero-benefit-icon {
              @apply tw-text-xl tw-text-orange-600;
            }
          }
        }
      }

      .club-command-bar {
        @apply tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-3;

        .command-identity {
          @apply tw-flex tw-min-w-0 tw-items-center tw-gap-2.5;

          .command-title {
            @apply tw-m-0 tw-truncate tw-text-lg tw-font-bold tw-text-neutral-900;
          }
        }

        .command-actions {
          @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2;

          .tab-control {
            @apply tw-flex tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

            .tab-option {
              @apply tw-min-h-10 tw-rounded-md tw-px-4 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

              &.active {
                @apply tw-border tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;
              }
            }
          }
        }
      }

      .paused-banner {
        @apply tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-amber-300 tw-bg-amber-50 tw-px-4 tw-py-3 tw-text-sm tw-text-amber-900;

        .paused-banner-icon {
          @apply tw-text-xl tw-text-amber-700;
        }
      }

      .club-tab-body {
        @apply tw-min-h-0;
      }
    }
  }

  .more-menu {
    @apply tw-flex tw-min-w-[200px] tw-flex-col tw-py-1;

    .more-menu-item {
      @apply tw-flex tw-min-h-11 tw-items-center tw-gap-2.5 tw-px-4 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;
    }
  }
</style>
